import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Empresa alvo da Area Master enquanto o suporte esta na ficha dela. */
export interface MasterContextoState {
  empresaId: number;
  nome: string;
}

/**
 * Estado em memoria da empresa sob a qual o suporte esta gerando cobranca.
 *
 * Fonte UNICA do header `X-Empresa-Contexto` do `MasterContextoInterceptor`, e existe por um
 * motivo estreito: as rotas que geram cobranca (QR e link da Bancard) resolvem a empresa la no
 * fundo, por `obterEmpresaId()`. Todo o resto da Area Master leva a empresa no path.
 *
 * Comeca vazio e morre ao sair da ficha da empresa (`NavigationStart`, antes de a tela seguinte
 * carregar): sem isso, uma tela qualquer poderia herdar o contexto e gravar na empresa errada.
 *
 * Espelha o `ContadorContextoService`, que resolve o mesmo problema na Area do Contador.
 */
@Injectable({ providedIn: 'root' })
export class MasterContextoService {
  /** O contexto so vive dentro da ficha de uma empresa. */
  private static readonly ROTAS_CONTEXTO: ReadonlyArray<string> = ['/master/empresas/'];

  private readonly empresaSubject = new BehaviorSubject<MasterContextoState | null>(null);

  /** Observable da empresa alvo (para a UI reagir). */
  readonly empresa$: Observable<MasterContextoState | null> = this.empresaSubject.asObservable();

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        if (this.empresaAtual() && !MasterContextoService.rotaPermiteContexto(e.url)) {
          this.limpar();
        }
      });
  }

  /** True quando a URL alvo e a ficha de uma empresa. */
  private static rotaPermiteContexto(url: string): boolean {
    const path = url.split('?')[0];
    return MasterContextoService.ROTAS_CONTEXTO.some((prefixo) => path.startsWith(prefixo));
  }

  /** Ativa o contexto sobre a empresa alvo (ao abrir a ficha dela). */
  selecionar(empresa: MasterContextoState): void {
    this.empresaSubject.next(empresa);
  }

  /** Desliga o contexto. Chamado ao sair da ficha e ao destruir a tela. */
  limpar(): void {
    if (this.empresaSubject.value !== null) {
      this.empresaSubject.next(null);
    }
  }

  /** Leitura sincrona, usada pelo interceptor a cada requisicao. */
  empresaAtual(): MasterContextoState | null {
    return this.empresaSubject.value;
  }
}
